import { ntxAPI } from '@API/axiosNeatex';
import { Card, CardBody, CardFooter } from '@components/card/card';
import useGetSuppliersList from '@hooks/NTX/Peoples/useGetSuppliersList';
import type { APIResponse } from '@store/Shared/shared';
import { TreeSelect } from 'antd';
import React, { useState } from 'react';
import { Table, Spinner, Form, Row, Col, Button, Alert, CardHeader } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Define types for our data
interface Invoice {
  invoiceNumber: string;
  supplierId: string;
  supplierName: string;
  supplierName2: string;
  invoiceDate: string;
  invoiceAmount: number;
  paidAmount: number;
  balanceAmount: number;
  status: 'Fully Paid' | 'Partially Paid';
}

const InvoiceReconciliationReport: React.FC = () => {
  const { treeSuppliersData } = useGetSuppliersList();
  const [data, setData] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [selectedSupplier, setSelectedSupplier] = useState<any>(''); // تركتها any لتجنب مشاكل null/string مع TreeSelect
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;

  // Function to fetch data with filters
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await ntxAPI.get<APIResponse<Invoice[]>>('Reports/GetInvoiceReconciliationReport', {
        params: { SupplierId: selectedSupplier, StartDate: startDate, EndDate: endDate }
      });

      if (data.succeeded === true) {
        setData(data.data);
      } else {
        setError(data instanceof Error ? data.message : 'حدث خطأ غير معروف');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'حدث خطأ غير معروف');
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setSelectedSupplier('all');
    setDateRange([null, null]);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US').format(amount);
  };

  const statusLabel = (s: Invoice['status']) => {
    return s === 'Fully Paid' ? 'مدفوعة بالكامل' : 'مدفوعة جزئياً';
  };

  return (
    <div className=' '>
      <Card className='border-0'>
        <CardBody>
          {error && (
            <Alert variant='danger' className='mb-4'>
              خطأ في تحميل البيانات: {error}
            </Alert>
          )}

          {/* Filters Section */}
          <Card className='mb-4'>
            <CardHeader className='d-flex justify-content-between align-items-center'>
              <div className='mb-0'>الفلاتر</div>
              {data.length > 0 && (
                <div>
                  <span className='me-2'>الإجمالي: {data.length} فاتورة</span>
                </div>
              )}
            </CardHeader>
            <CardBody>
              <Form>
                <Row className='g-3'>
                  <Col md={4}>
                    <Form.Group controlId='supplierFilter'>
                      <Form.Label>المورد</Form.Label>

                      <TreeSelect
                        showSearch
                        className='w-100'
                        treeLine
                        allowClear
                        treeDefaultExpandAll={false}
                        value={selectedSupplier}
                        onChange={(option: any) => {
                          setSelectedSupplier(option === null ? null : option);
                        }}
                        treeData={treeSuppliersData}
                        treeNodeFilterProp='title'
                        placeholder='اختر المورد'
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group controlId='dateRangeFilter'>
                      <Form.Label>الفترة</Form.Label>
                      <DatePicker
                        allowSameDay
                        selectsRange={true}
                        startDate={startDate}
                        endDate={endDate}
                        onChange={update => setDateRange(update)}
                        className='form-control text-center'
                        placeholderText='اختر الفترة'
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} className='text-end'>
                    <Button variant='secondary' onClick={resetFilters} className='me-2'>
                      تصفير الفلاتر
                    </Button>

                    <Button variant='primary' onClick={fetchData} disabled={loading}>
                      {loading ? (
                        <>
                          <Spinner
                            as='span'
                            animation='border'
                            size='sm'
                            role='status'
                            aria-hidden='true'
                            className='me-2'
                          />
                          جاري التحميل...
                        </>
                      ) : (
                        'تحميل البيانات'
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>

          {/* Results Section */}
          {loading ? (
            <div className='text-center my-5'>
              <Spinner animation='border' role='status'>
                <span className='visually-hidden'>جاري التحميل...</span>
              </Spinner>
              <p className='mt-2'>جاري تحميل بيانات التقرير...</p>
            </div>
          ) : data.length > 0 ? (
            <div className='table-responsive'>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>رقم الفاتورة</th>
                    <th>المورد</th>
                    <th>التاريخ</th>
                    <th>قيمة الفاتورة</th>
                    <th>المبلغ المدفوع</th>
                    <th>الرصيد</th>
                    <th>الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={`${item.invoiceNumber}-${index}`}>
                      <td>{item.invoiceNumber}</td>
                      <td>{item.supplierName}</td>
                      <td>{new Date(item.invoiceDate).toLocaleDateString('en-US')}</td>
                      <td>{formatCurrency(item.invoiceAmount)}</td>
                      <td>{formatCurrency(item.paidAmount)}</td>
                      <td
                        className={
                          item.balanceAmount < 0
                            ? 'text-danger'
                            : item.balanceAmount > 0
                            ? 'text-warning'
                            : 'text-success'
                        }
                      >
                        {formatCurrency(item.balanceAmount)}
                      </td>
                      <td>
                        <span
                          className={`badge ${item.status === 'Fully Paid' ? 'bg-success' : 'bg-warning text-dark'}`}
                        >
                          {statusLabel(item.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className='text-center py-5'>
              <p>لا توجد بيانات. اختر الفلاتر ثم اضغط "تحميل البيانات" لعرض التقرير.</p>
            </div>
          )}
        </CardBody>

        {data.length > 0 && (
          <CardFooter className='text-muted'>تم إنشاء التقرير بتاريخ {new Date().toLocaleString('en-US')}</CardFooter>
        )}
      </Card>
    </div>
  );
};

export default InvoiceReconciliationReport;
